import React, { useState, useEffect } from "react";
import {
  Box, Paper, Typography, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Grid, Alert, Snackbar,
  IconButton, Tooltip, Chip, FormControl, InputLabel, Select, MenuItem,
  Card, CardContent, InputAdornment, TablePagination
} from "@mui/material";
import {
  Add, Edit, Delete, Search, Refresh, Inventory, ShoppingCart,
  AttachMoney, Category, CheckCircle, Cancel
} from "@mui/icons-material";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "";

const IVA_OPTIONS = ["0%", "15%", "No Objeto", "Exento"];

const emptyForm = {
  codigo: "", codigo_auxiliar_sku: "", nombre: "",
  tipo: "PRODUCTO",
  precio_1: "", precio_2: "", precio_3: "",
  iva_porcentaje: "15%",
};

export default function Productos() {
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editando, setEditando] = useState(false);
  const [productoSel, setProductoSel] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [busqueda, setBusqueda] = useState("");
  const [filtroTipo, setFiltroTipo] = useState("todos");
  const [formData, setFormData] = useState({ ...emptyForm });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return { Authorization: `Bearer ${token}` };
  };

  useEffect(() => { cargarProductos(); }, [busqueda, filtroTipo]);

  const cargarProductos = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (busqueda) params.append("busqueda", busqueda);
      if (filtroTipo !== "todos") params.append("tipo", filtroTipo);
      const res = await axios.get(`${API_URL}/api/productos?${params}`, { headers: getAuthHeaders() });
      setProductos(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      setProductos([]);
      showSnackbar("Error al cargar productos", "error");
    } finally { setLoading(false); }
  };

  const handleInput = (campo, valor) => setFormData(prev => ({ ...prev, [campo]: valor }));

  const abrirDialogo = (producto = null) => {
    if (producto) {
      setEditando(true);
      setProductoSel(producto);
      setFormData({
        codigo: producto.codigo || "",
        codigo_auxiliar_sku: producto.codigo_auxiliar_sku || "",
        nombre: producto.nombre || "",
        tipo: producto.tipo || "PRODUCTO",
        precio_1: producto.precio_1 != null ? String(producto.precio_1) : (producto.precioNormal != null ? String(producto.precioNormal) : ""),
        precio_2: producto.precio_2 != null ? String(producto.precio_2) : "",
        precio_3: producto.precio_3 != null ? String(producto.precio_3) : "",
        iva_porcentaje: producto.iva_porcentaje || (producto.iva === "0" || producto.iva === 0 ? "0%" : "15%"),
      });
    } else {
      setEditando(false);
      setProductoSel(null);
      setFormData({ ...emptyForm });
    }
    setDialogOpen(true);
  };

  const cerrarDialogo = () => { setDialogOpen(false); setEditando(false); setProductoSel(null); };

  const guardarProducto = async () => {
    if (!formData.codigo || !formData.nombre) {
      return showSnackbar("Código y Nombre son requeridos", "error");
    }
    if (!formData.precio_1 || parseFloat(formData.precio_1) <= 0) {
      return showSnackbar("El Precio 1 (PVP) debe ser mayor a 0", "error");
    }
    try {
      const payload = {
        ...formData,
        precio_1: parseFloat(formData.precio_1 || 0),
        precio_2: parseFloat(formData.precio_2 || 0),
        precio_3: parseFloat(formData.precio_3 || 0),
      };
      const url = editando ? `${API_URL}/api/productos/${productoSel.id}` : `${API_URL}/api/productos`;
      const method = editando ? "put" : "post";
      await axios[method](url, payload, { headers: getAuthHeaders() });
      showSnackbar(editando ? "Producto actualizado" : "Producto creado", "success");
      cerrarDialogo();
      cargarProductos();
    } catch (error) {
      showSnackbar(error.response?.data?.error || "Error al guardar producto", "error");
    }
  };

  const showSnackbar = (message, severity) => setSnackbar({ open: true, message, severity });

  // Stats
  const stats = {
    total: productos.length,
    productosCount: productos.filter(p => p.tipo === "PRODUCTO").length,
    serviciosCount: productos.filter(p => p.tipo === "SERVICIO").length,
    stockTotal: productos.reduce((s, p) => s + (p.tipo === "PRODUCTO" ? parseFloat(p.cantidad_stock || p.stock_actual || 0) : 0), 0),
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Typography variant="h5" fontWeight={700}>Productos</Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button variant="outlined" startIcon={<Refresh />} onClick={cargarProductos}>Actualizar</Button>
            <Button variant="contained" startIcon={<Add />} onClick={() => abrirDialogo()}>Nuevo Producto</Button>
          </Box>
        </Box>

        {/* Filtros */}
        <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
          <TextField placeholder="Buscar por código, SKU o nombre..." value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            InputProps={{ startAdornment: <Search sx={{ mr: 1, color: "text.secondary" }} /> }}
            sx={{ flexGrow: 1 }} size="small" />
          <FormControl sx={{ minWidth: 140 }} size="small">
            <InputLabel>Tipo</InputLabel>
            <Select value={filtroTipo} label="Tipo" onChange={(e) => setFiltroTipo(e.target.value)}>
              <MenuItem value="todos">Todos</MenuItem>
              <MenuItem value="PRODUCTO">Productos</MenuItem>
              <MenuItem value="SERVICIO">Servicios</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Tabla */}
        {loading ? <Typography>Cargando...</Typography> : productos.length === 0 ? (
          <Alert severity="info">No hay productos registrados.</Alert>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                    <TableCell><strong>Código</strong></TableCell>
                    <TableCell><strong>SKU</strong></TableCell>
                    <TableCell><strong>Nombre</strong></TableCell>
                    <TableCell><strong>Tipo</strong></TableCell>
                    <TableCell align="right"><strong>PVP</strong></TableCell>
                    <TableCell align="right"><strong>Mayorista</strong></TableCell>
                    <TableCell align="right"><strong>Especial</strong></TableCell>
                    <TableCell align="center"><strong>IVA</strong></TableCell>
                    <TableCell align="right"><strong>Costo Compra</strong></TableCell>
                    <TableCell align="center"><strong>Stock</strong></TableCell>
                    <TableCell align="center"><strong>Acciones</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {productos.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((p) => {
                    const stock = parseFloat(p.cantidad_stock || p.stock_actual || 0);
                    const costo = parseFloat(p.costo_ultima_compra || 0);
                    const pvp = parseFloat(p.precio_1 || p.precio_venta || p.precioNormal || 0);
                    const p2 = parseFloat(p.precio_2 || 0);
                    const p3 = parseFloat(p.precio_3 || 0);
                    const iva = p.iva_porcentaje || (p.iva === "0" || p.iva === 0 ? "0%" : "15%");
                    return (
                      <TableRow key={p.id} hover>
                        <TableCell><Typography variant="body2" fontFamily="monospace" fontWeight={600}>{p.codigo || "—"}</Typography></TableCell>
                        <TableCell><Typography variant="caption" color="text.secondary">{p.codigo_auxiliar_sku || "—"}</Typography></TableCell>
                        <TableCell><Typography variant="body2" fontWeight={500}>{p.nombre}</Typography></TableCell>
                        <TableCell>
                          <Chip icon={p.tipo === "SERVICIO" ? <ShoppingCart /> : <Inventory />}
                            label={p.tipo === "SERVICIO" ? "Servicio" : "Producto"}
                            color={p.tipo === "SERVICIO" ? "secondary" : "primary"} size="small" />
                        </TableCell>
                        <TableCell align="right"><strong>${pvp.toFixed(2)}</strong></TableCell>
                        <TableCell align="right">{p2 > 0 ? `$${p2.toFixed(2)}` : "—"}</TableCell>
                        <TableCell align="right">{p3 > 0 ? `$${p3.toFixed(2)}` : "—"}</TableCell>
                        <TableCell align="center"><Chip label={iva} size="small" color={iva === "15%" ? "warning" : "default"} /></TableCell>
                        <TableCell align="right"><Typography variant="caption" color="text.secondary">${costo.toFixed(2)}</Typography></TableCell>
                        <TableCell align="center">
                          {p.tipo === "SERVICIO" ? <Typography variant="caption" color="text.secondary">N/A</Typography> : (
                            <Chip label={stock} size="small" color={stock < 5 ? "error" : stock < 20 ? "warning" : "success"} />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Editar"><IconButton size="small" onClick={() => abrirDialogo(p)}><Edit /></IconButton></Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination rowsPerPageOptions={[10, 25, 50]} component="div"
              count={productos.length} rowsPerPage={rowsPerPage} page={page}
              onPageChange={(_, p) => setPage(p)}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
              labelRowsPerPage="Filas:" />
          </>
        )}
      </Paper>

      {/* Dialog Crear/Editar */}
      <Dialog open={dialogOpen} onClose={cerrarDialogo} maxWidth="md" fullWidth>
        <DialogTitle>{editando ? "Editar Producto" : "Nuevo Producto"}</DialogTitle>
        <DialogContent>
          <Box sx={{ py: 2 }}>
            {/* 1. Identificación Principal */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">1. Identificación Principal</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth required label="Código (único)" value={formData.codigo}
                      onChange={(e) => handleInput("codigo", e.target.value)}
                      disabled={editando} helperText="Identificador principal" />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth label="Código Auxiliar / SKU" value={formData.codigo_auxiliar_sku}
                      onChange={(e) => handleInput("codigo_auxiliar_sku", e.target.value)}
                      helperText="Código de barras o SKU interno" />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    {/* Spacer for alignment */}
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth required label="Nombre / Descripción" value={formData.nombre}
                      onChange={(e) => handleInput("nombre", e.target.value)}
                      helperText="Descripción detallada del producto o servicio" />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* 2. Clasificación */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">2. Clasificación</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>Tipo</InputLabel>
                      <Select value={formData.tipo} label="Tipo"
                        onChange={(e) => handleInput("tipo", e.target.value)}>
                        <MenuItem value="PRODUCTO">Producto</MenuItem>
                        <MenuItem value="SERVICIO">Servicio</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <Alert severity="info" sx={{ height: "100%", display: "flex", alignItems: "center" }}>
                      {formData.tipo === "PRODUCTO"
                        ? "El sistema habilitará control de inventario (stock aumenta con compras, disminuye con ventas)."
                        : "El sistema ignorará el stock. No se controla inventario para servicios."}
                    </Alert>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* 3. Estructura de Precios */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">3. Estructura de Precios</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth required label="Precio 1 (PVP)" value={formData.precio_1}
                      onChange={(e) => handleInput("precio_1", e.target.value.replace(/[^0-9.]/g, ""))}
                      InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                      helperText="Precio de venta al público" />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth label="Precio 2 (Mayorista)" value={formData.precio_2}
                      onChange={(e) => handleInput("precio_2", e.target.value.replace(/[^0-9.]/g, ""))}
                      InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                      helperText="Precio para mayoristas" />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth label="Precio 3 (Especial)" value={formData.precio_3}
                      onChange={(e) => handleInput("precio_3", e.target.value.replace(/[^0-9.]/g, ""))}
                      InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                      helperText="Precio oferta o clientes especiales" />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* 4. Impuestos */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">4. Configuración de Impuestos</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>IVA</InputLabel>
                      <Select value={formData.iva_porcentaje} label="IVA"
                        onChange={(e) => handleInput("iva_porcentaje", e.target.value)}>
                        {IVA_OPTIONS.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* 5. Inventario y Costos (solo lectura en edición) */}
            {editando && productoSel && (
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary">5. Inventario y Costos</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField fullWidth label="Costo Última Compra" disabled
                        value={`$${parseFloat(productoSel.costo_ultima_compra || 0).toFixed(2)}`}
                        helperText="Se actualiza automáticamente al registrar compras" />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField fullWidth label="Cantidad en Stock" disabled
                        value={productoSel.tipo === "SERVICIO" ? "N/A" : parseFloat(productoSel.cantidad_stock || productoSel.stock_actual || 0)}
                        helperText="Aumenta con compras, disminuye con ventas" />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={cerrarDialogo}>Cancelar</Button>
          <Button onClick={guardarProducto} variant="contained">
            {editando ? "Actualizar" : "Crear"} Producto
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
